import React, { PropsWithChildren, useEffect, useMemo } from 'react';

import { ApolloClient, ApolloProvider, gql, useApolloClient } from '@apollo/client';
import { AuthContextProvider, GraphQLClient } from '@opendoor/auth-fe';
import { AddContextualData, addDatadogRUMGlobalContext, analytics } from '@opendoor/observability';
import { clientFromUrl } from 'setupClient';

import { RAILS_ENV, useBrowserCompatibilityData } from 'shared/helpers/globals';

import { ReauthProvider } from 'platform/components/ReauthProvider';
import { useObservability } from 'platform/observability';

import { useAuth } from './auth-fe';

/** Top-level providers that are applicable to all of Cake. */
export const RootProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const client = useMemo(() => clientFromUrl(), []);
  const appFramework = 'remix';
  addDatadogRUMGlobalContext({ framework: appFramework });
  return (
    <AddContextualData data={{ framework: appFramework }}>
      <ApolloProvider client={client}>
        <RootAuthProvider>
          <ReauthProvider>{children}</ReauthProvider>
        </RootAuthProvider>
      </ApolloProvider>
    </AddContextualData>
  );
};

const authQueryClient: <T>(client: ApolloClient<T>) => GraphQLClient = (client) => ({
  async query<TData, TVars>(query: string, vars: TVars) {
    const result = await client.query<TData, TVars>({
      query: gql(query),
      context: { noBatch: true },
      fetchPolicy: 'network-only',
      variables: vars,
    });
    return {
      data: result.data,
      error: result.error,
    };
  },
});

const RootAuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const client = useApolloClient();
  const authApolloClient = useMemo(() => authQueryClient(client), [client]);
  const { trackError } = useObservability();

  return (
    <AuthContextProvider env={RAILS_ENV} client={authApolloClient} trackError={trackError}>
      <AuthContextualData>{children}</AuthContextualData>
    </AuthContextProvider>
  );
};

const AuthContextualData: React.FC<PropsWithChildren> = ({ children }) => {
  const { user, state } = useAuth();
  useEffect(() => {
    if (!user) {
      return;
    }
    addDatadogRUMGlobalContext({
      hasOpendoorEmail: user.hasOpendoorEmail,
      hasOpendoorInternalAccess: user.hasOpendoorInternalAccess,
    });
    analytics.registerWithUserID(user.customerUuid ?? '', { ...user });
  }, [user]);
  const { isOpendoorAppEvoWebView } = useBrowserCompatibilityData();

  return (
    <AddContextualData
      user={
        user
          ? {
              humanID: user.humanToken,
              email: user.email,
            }
          : undefined
      }
      data={{
        authenticationState: state,
        // This ensures the user data is sent to segment/amplitude
        humanToken: user?.humanToken,
        email: user?.email,
        hasOpendoorEmail: user?.hasOpendoorEmail,
        hasOpendoorInternalAccess: user?.hasOpendoorInternalAccess,
        isMobileNativeWebView: isOpendoorAppEvoWebView,
      }}
      customerContext={user?.customerUuid ? { customerUuid: user.customerUuid } : undefined}
    >
      {children}
    </AddContextualData>
  );
};
